/* ==========================================================================
   Cart Rewards - collapse the reward cards while the cart list is scrolled
   --------------------------------------------------------------------------
   The reward cards sit ABOVE the drawer's scrolling item list rather than
   inside it, so they never scroll away. Good for visibility, but on a phone
   the two cards were taking about 240px of a ~660px drawer, leaving very
   little room for the products themselves.

   Once the customer scrolls the item list we mark the stack collapsed and
   assets/cart-rewards.css + assets/cart-tier.css fold each card down - message
   and progress bar only. Scrolling back to the top restores them. The CSS
   applies the collapse on small screens only; a desktop drawer has the room,
   and folding cards away under someone's cursor would just be startling.

   TWO THINGS THIS FILE HAS TO BE CAREFUL ABOUT
   --------------------------------------------------------------------------
   1. Collapsing GIVES the list back height. If the list only just overflowed,
      collapsing can remove the overflow entirely - the browser then clamps
      scrollTop back to 0, we see 0, we un-collapse, and the list springs back
      to the top. To the customer the drawer simply refuses to scroll. So:
      collapse, measure, and if the customer's scroll position no longer fits,
      put it straight back and stop trying for this cart. Measured repro:
      390x660 with 2 products, scrollTop 60 of a possible 138 -> settled at 0.

   2. Below 700px of viewport height the theme moves the scrollbar: the item
      list goes `overflow: visible` and `.cart-drawer__inner` becomes the
      scroller (component-cart-drawer.css). There the cards scroll out of view
      on their own, so collapsing them buys nothing and only risks (1). We sit
      that case out entirely.

   Deliberately standalone and dependency-free:

   - One capture-phase scroll listener on the document. Scroll events don't
     bubble, but they DO capture, so this survives the theme re-rendering the
     drawer wholesale on every cart update - which it does, and which would
     defeat a listener bound directly to the scrolling element.
   - No reliance on CartRewardsCore, so load order doesn't matter.
   - If anything is missing it simply does nothing, and the cards behave
     exactly as they did before.
   ========================================================================== */
(function () {
  'use strict';

  var COLLAPSE_AT = 24; /* px of scroll before the cards fold */
  var EXPAND_AT = 2; /* effectively "back at the top" */
  var SAFETY = 4; /* px of headroom, the cards' padding transition is ~3px */
  var ATTR = 'data-cart-rewards-collapsed';
  var DRAWER_SELECTOR = 'cart-drawer, .cart-drawer, .drawer--cart, #CartDrawer';
  var LIST_SELECTOR = 'cart-drawer-items, .cart-drawer__items, .drawer__cart-items';

  /* Set once the verify step has shown that collapsing this particular cart
     would cost the customer their scroll position. Cleared whenever the cart
     re-renders or the viewport changes, i.e. whenever the sums change. */
  var blocked = false;

  var pending = false;
  var queued = null;

  function maxScroll(el) {
    return el.scrollHeight - el.clientHeight;
  }

  function apply() {
    pending = false;
    var job = queued;
    queued = null;
    if (!job) return;

    var stack = job.stack;
    var scroller = job.scroller;
    if (!stack || !stack.isConnected || !scroller || !scroller.isConnected) return;

    var collapsed = stack.getAttribute(ATTR) === 'true';
    var top = scroller.scrollTop;

    if (!collapsed) {
      if (blocked || top <= COLLAPSE_AT) return;

      /* Collapse, then check the customer can still be where they are. Reading
         scrollHeight forces the layout, so this is measured, not predicted. */
      stack.setAttribute(ATTR, 'true');
      if (maxScroll(scroller) < top + SAFETY) {
        stack.setAttribute(ATTR, 'false');
        scroller.scrollTop = top; /* the read above may have clamped it */
        blocked = true;
      }
      return;
    }

    /* Only unfold once they are genuinely back at the top. Anything looser and
       a clamp reads as "scrolled to the top" and we start flip-flopping. */
    if (top <= EXPAND_AT) stack.setAttribute(ATTR, 'false');
  }

  function schedule(stack, scroller) {
    queued = { stack: stack, scroller: scroller };
    if (pending) return;
    pending = true;
    if (window.requestAnimationFrame) window.requestAnimationFrame(apply);
    else setTimeout(apply, 60);
  }

  function onScroll(event) {
    var scroller = event.target;
    if (!scroller || scroller.nodeType !== 1 || !scroller.closest) return;

    /* Only the cart drawer. The page itself scrolling is not our business. */
    var drawer = scroller.closest(DRAWER_SELECTOR);
    if (!drawer) return;

    /* Only when the ITEM LIST is the thing scrolling. On short viewports the
       theme makes the whole drawer panel the scroller and the cards scroll
       away by themselves - see note 2 at the top. */
    if (!scroller.matches(LIST_SELECTOR)) return;

    var stack = drawer.querySelector('.cart-rewards-stack');
    if (!stack) return;

    schedule(stack, scroller);
  }

  /* Capture phase is the whole trick - see the note at the top of the file. */
  try {
    document.addEventListener('scroll', onScroll, { capture: true, passive: true });
  } catch (e) {
    /* Older browsers without options-object support. */
    document.addEventListener('scroll', onScroll, true);
  }

  function reset() {
    blocked = false;
    var stacks = document.querySelectorAll('.cart-rewards-stack[' + ATTR + "='true']");
    for (var i = 0; i < stacks.length; i++) stacks[i].setAttribute(ATTR, 'false');
  }

  /* A freshly re-rendered drawer starts at scrollTop 0, so clear any stale
     collapsed state rather than leaving the cards folded with nothing
     scrolled - and re-open the question of whether collapsing is worth it,
     because the cart just changed size. Same on resize/rotate. */
  document.addEventListener('shopify:section:load', reset);
  window.addEventListener('resize', reset);
  window.addEventListener('orientationchange', reset);
})();

/* ==========================================================================
   Cart Rewards - free-gift SIZE PICKER  (append to collapse module)
   --------------------------------------------------------------------------
   Adds variant (size) selection to the free-gift card when, and only when, a
   gift product has more than one variant. Single-variant gifts (e.g. socks)
   are left completely untouched - the existing Claim / auto-add path handles
   them exactly as before, so this block is a no-op for the current gift pool.

   Why it lives here: the gift markup is rendered inside a <template> in
   sections/cart-rewards-ladder.liquid and only becomes live DOM once
   cart-rewards.js clones it into the drawer / cart page, so the behaviour has
   to run from a loaded asset. This module is already loaded and already
   watches the same DOM, so the size picker rides along rather than adding a
   fourth <script> tag (which would mean editing the section).

   How it works, kept deliberately self-contained:
   - Reads each gift product's variants from Shopify's own /products/{handle}.js
     (cached), so no theme file has to render variant data.
   - Injects its own <style> and a .arl-sizes chip row into the featured card,
     using the card's existing --arl-* tokens so it matches every theme.
   - For a multi-variant gift, Claim is intercepted in the capture phase: the
     first tap reveals the sizes, and once a size is chosen the tap adds THAT
     variant via window.__arlInstance.addGift(). The collection-scoped gift
     discount zeroes any variant of a gift product, so the chosen size checks
     out free.
   - Auto-add is suppressed only while the featured gift is multi-variant, so
     the cart never silently adds a size the customer did not choose.
   - Everything is wrapped so a failure can only mean "no size picker", never a
     broken cart.
   ========================================================================== */
(function () {
  'use strict';

  var STYLE_ID = 'arl-sizes-style';
  var cache = {};      /* handle -> array of {id,title,available} */
  var pending = {};    /* handle -> true while a fetch is in flight */
  var applying = false;
  var rafId = null;

  function copy(key, fallback) {
    try {
      var c = window.__arlInstance && window.__arlInstance.cfg && window.__arlInstance.cfg.copy;
      return (c && c[key]) || fallback;
    } catch (e) {
      return fallback;
    }
  }

  function unlockedState() {
    try {
      var s = window.__arlInstance && window.__arlInstance.lastState;
      return { unlocked: !!(s && s.giftUnlocked), inCart: !!(s && s.giftInCart) };
    } catch (e) {
      return { unlocked: false, inCart: false };
    }
  }

  function injectStyle() {
    if (document.getElementById(STYLE_ID)) return;
    var css =
      '.arl-sizes{display:flex;flex-wrap:wrap;gap:6px;margin-top:10px;}' +
      '.arl-sizes[hidden]{display:none;}' +
      ".arl-sizes__label{flex:0 0 100%;font-size:max(9px,calc(var(--arl-font-size) - 3px));letter-spacing:var(--arl-letter-spacing);text-transform:var(--arl-transform);color:var(--arl-text-2);margin-bottom:1px;}" +
      '.arl-size{appearance:none;-webkit-appearance:none;font-family:inherit;font-size:max(10px,calc(var(--arl-font-size) - 1px));line-height:1;padding:6px 10px;cursor:pointer;background:transparent;color:var(--arl-text);border:1px solid var(--arl-marker-upcoming);border-radius:calc(var(--arl-radius-card) / 2);transition:background .16s ease,color .16s ease,border-color .16s ease;}' +
      '.arl-size:hover:not([aria-disabled=\'true\']){border-color:var(--arl-text-2);}' +
      ".arl-size[aria-checked='true']{background:var(--arl-accent);color:var(--arl-on-accent);border-color:var(--arl-accent);}" +
      ".arl-size[aria-disabled='true']{opacity:.4;cursor:not-allowed;text-decoration:line-through;}" +
      '.arl-size:focus-visible{outline:2px solid var(--arl-accent);outline-offset:2px;}';
    var el = document.createElement('style');
    el.id = STYLE_ID;
    el.textContent = css;
    (document.head || document.documentElement).appendChild(el);
  }

  function handleFromUrl(url) {
    if (!url) return null;
    var clean = String(url).split('?')[0].split('#')[0];
    var m = clean.match(/\/products\/([^\/]+)\/?$/);
    return m ? m[1] : null;
  }

  function fetchVariants(handle, cb) {
    if (!handle) { cb(null); return; }
    if (cache[handle]) { cb(cache[handle]); return; }
    if (pending[handle]) { cb(null); return; }
    pending[handle] = true;
    var root =
      window.Shopify && window.Shopify.routes && window.Shopify.routes.root
        ? window.Shopify.routes.root
        : '/';
    fetch(root.replace(/\/$/, '') + '/products/' + handle + '.js', {
      headers: { Accept: 'application/json' },
      credentials: 'same-origin'
    })
      .then(function (r) { return r.ok ? r.json() : null; })
      .then(function (p) {
        pending[handle] = false;
        if (!p || !p.variants) { cache[handle] = []; cb(cache[handle]); return; }
        cache[handle] = p.variants.map(function (v) {
          return {
            id: String(v.id),
            title: v.title,
            available: v.available !== false
          };
        });
        cb(cache[handle]);
      })
      .catch(function () {
        pending[handle] = false;
        cache[handle] = [];
        cb(cache[handle]);
      });
  }

  function poolItems(card) {
    var pool = card.querySelector('[data-arl-pool]');
    return pool ? [].slice.call(pool.querySelectorAll('[data-arl-pool-item]')) : [];
  }

  function activeItem(card) {
    if (card.__arlActive && card.__arlActive.isConnected) return card.__arlActive;
    var checked = card.querySelector('[data-arl-pool-item][aria-checked="true"]');
    if (checked) return checked;
    var items = poolItems(card);
    return items.length ? items[0] : null;
  }

  function variantsFor(item) {
    if (!item) return null;
    var h = handleFromUrl(item.getAttribute('data-url'));
    return h ? cache[h] : null;
  }

  function activeIsMulti(card) {
    var v = variantsFor(activeItem(card));
    return !!(v && v.length > 1);
  }

  function ensureSizeRow(card) {
    var row = card.querySelector('.arl-sizes');
    if (row) return row;
    row = document.createElement('div');
    row.className = 'arl-sizes';
    row.setAttribute('data-arl-sizes', '');
    row.setAttribute('role', 'radiogroup');
    row.setAttribute('aria-label', 'Choose size');
    row.hidden = true;
    var anchor = card.querySelector('.arl-gift__row');
    if (anchor && anchor.parentNode) anchor.parentNode.insertBefore(row, anchor.nextSibling);
    else card.appendChild(row);
    return row;
  }

  function renderChips(card, row, variants) {
    var sig = variants
      .map(function (v) { return v.id + ':' + (v.available ? '1' : '0'); })
      .join('|');
    if (row.getAttribute('data-sig') === sig) return; /* unchanged - keep selection */
    row.setAttribute('data-sig', sig);
    row.innerHTML = '';
    var label = document.createElement('span');
    label.className = 'arl-sizes__label';
    label.textContent = 'Choose size';
    row.appendChild(label);
    variants.forEach(function (v) {
      var b = document.createElement('button');
      b.type = 'button';
      b.className = 'arl-size';
      b.setAttribute('role', 'radio');
      b.setAttribute('data-variant-id', v.id);
      b.setAttribute('data-title', v.title);
      b.setAttribute('aria-checked', 'false');
      if (!v.available) b.setAttribute('aria-disabled', 'true');
      b.textContent = v.title;
      row.appendChild(b);
    });
    card.__arlChosenId = null;
  }

  function setChosen(card, id, title) {
    card.__arlChosenId = id;
    card.__arlChosenTitle = title;
    var row = card.querySelector('.arl-sizes');
    if (!row) return;
    [].forEach.call(row.querySelectorAll('.arl-size'), function (c) {
      c.setAttribute('aria-checked', c.getAttribute('data-variant-id') === id ? 'true' : 'false');
    });
  }

  function enhance(card) {
    if (!card || applying) return;
    var items = poolItems(card);
    if (!items.length) return;

    /* Prefetch every pool product's variants so click interception can decide
       synchronously later. */
    items.forEach(function (it) {
      var h = handleFromUrl(it.getAttribute('data-url'));
      if (h && !cache[h] && !pending[h]) fetchVariants(h, function () { schedule(); });
    });

    var active = activeItem(card);
    var variants = variantsFor(active);
    if (!variants) return; /* not fetched yet - a later pass will handle it */

    var state = unlockedState();
    var row = card.querySelector('.arl-sizes');

    applying = true;
    try {
      if (variants.length <= 1) {
        /* Single-variant gift: nothing to choose. Leave the native path alone. */
        if (row) row.hidden = true;
        card.__arlChosenId = null;
      } else {
        row = ensureSizeRow(card);
        renderChips(card, row, variants);
        row.hidden = !(state.unlocked && !state.inCart);

        var claim = card.querySelector('[data-arl-claim]');
        if (claim && state.unlocked && !state.inCart) {
          if (card.__arlChosenId) {
            var claimTxt = copy('claim', 'Claim');
            if (claim.textContent !== claimTxt) claim.textContent = claimTxt;
            claim.disabled = false;
          } else {
            var chooseTxt = 'Choose size';
            if (claim.textContent !== chooseTxt) claim.textContent = chooseTxt;
            claim.disabled = false;
          }
        }
      }

      /* Suppress auto-add only while the FEATURED (first) gift is multi-variant,
         so a default size is never added without a choice. */
      var featuredVariants = variantsFor(items[0]);
      if (
        featuredVariants &&
        featuredVariants.length > 1 &&
        state.unlocked &&
        !state.inCart &&
        window.__arlInstance
      ) {
        window.__arlInstance.autoAddDone = true;
      }
    } catch (e) {
    } finally {
      applying = false;
    }
  }

  function enhanceAll() {
    rafId = null;
    var cards = document.querySelectorAll('[data-arl-gift]');
    for (var i = 0; i < cards.length; i++) {
      try { enhance(cards[i]); } catch (e) {}
    }
  }

  function schedule() {
    if (rafId) return;
    if (window.requestAnimationFrame) rafId = window.requestAnimationFrame(enhanceAll);
    else rafId = setTimeout(enhanceAll, 60);
  }

  /* Clicks - capture phase, so we run before cart-rewards.js's own handlers and
     can stop them for the multi-variant case. */
  document.addEventListener(
    'click',
    function (e) {
      var t = e.target;
      if (!t || !t.closest) return;

      var chip = t.closest('.arl-size');
      if (chip) {
        if (chip.getAttribute('aria-disabled') === 'true') { e.preventDefault(); return; }
        var chipCard = chip.closest('[data-arl-gift]');
        if (chipCard) setChosen(chipCard, chip.getAttribute('data-variant-id'), chip.getAttribute('data-title'));
        schedule();
        return;
      }

      var claim = t.closest('[data-arl-claim]');
      if (claim) {
        var claimCard = claim.closest('[data-arl-gift]');
        if (claimCard && activeIsMulti(claimCard)) {
          e.preventDefault();
          e.stopImmediatePropagation();
          if (claimCard.__arlChosenId && window.__arlInstance) {
            window.__arlInstance.addGift(claimCard.__arlChosenId, claimCard.__arlChosenTitle, 'claim');
          } else {
            var row = claimCard.querySelector('.arl-sizes');
            if (row) row.hidden = false;
          }
        }
        return; /* single-variant: let the native handler add it */
      }

      var item = t.closest('[data-arl-pool-item]');
      if (item && !item.disabled) {
        var itemCard = item.closest('[data-arl-gift]');
        var v = variantsFor(item);
        if (itemCard && v && v.length > 1) {
          /* Multi-variant gift chosen from the pool: make it the featured gift
             and let the customer pick a size instead of adding a default one. */
          e.preventDefault();
          e.stopImmediatePropagation();
          itemCard.__arlActive = item;
          itemCard.__arlChosenId = null;
          var titleEl = itemCard.querySelector('[data-arl-gift-title]');
          if (titleEl) titleEl.textContent = item.getAttribute('data-product-title') || titleEl.textContent;
          var imgEl = itemCard.querySelector('img.arl-gift__img');
          var imgSrc = item.getAttribute('data-image');
          if (imgEl && imgSrc) { imgEl.removeAttribute('srcset'); imgEl.setAttribute('src', imgSrc); }
          schedule();
        }
        /* single-variant: let the native handler add it */
      }
    },
    true
  );

  var mo = new MutationObserver(function () {
    if (applying) return;
    schedule();
  });
  try {
    mo.observe(document.body, { childList: true, subtree: true });
  } catch (e) {}

  document.addEventListener('shopify:section:load', schedule);
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () { injectStyle(); schedule(); }, { once: true });
  } else {
    injectStyle();
    schedule();
  }
  injectStyle();
})();